import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import CustomButton from '@/components/CustomButton'
import EmptyState from '@/components/EmptyState'
import RefreshList from '@/components/RefreshList'
import Ripple from '@/components/Ripple'
import SkeletonBox from '@/components/SkeletonBox'
import FixedBottom from '@/components/FixedBottom'
import { APIWeb } from '@/api'

// KYC状态枚举（对应后端返回的状态值）
export enum CardholderKycStatus {
  Pending = 'pending_audit',      // 审核中
  Approved = 'pass_audit',        // 预审核通过
  Rejected = 'reject_audit',      // 审核失败
}

// 根据KYC状态获取标签样式（text 是翻译键，需要在组件内使用 t 函数翻译）
const getStatusStyle = (status?: string) => {
  switch (status) {
    case CardholderKycStatus.Pending:
      return {
        textKey: '审核中',
        bgColor: 'bg-white',
        textColor: 'text-[var(--primary-color)]',
        borderColor: 'border-[var(--primary-color)]'
      }
    case CardholderKycStatus.Rejected:
      return {
        textKey: '审核失败',
        bgColor: 'bg-white',
        textColor: 'text-[#EF454A]',
        borderColor: 'border-[#EF454A]'
      }
    case CardholderKycStatus.Approved:
    default:
      return {
        textKey: '预审核通过',
        bgColor: 'bg-white',
        textColor: 'text-[#2AB26C]',
        borderColor: 'border-[#2AB26C]'
      }
      
  }
}

// 最大持卡人数量
const MAX_CARDHOLDER_COUNT = 5

export default function CardholderList() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [cardholders, setCardholders] = useState<CardholderKycListItem[]>([])

  // 获取持卡人列表
  const fetchCardholders = useCallback(async () => {
    try {
      setLoading(true)
      const response = await APIWeb.getCardholderKycListV2({})
      setCardholders(response?.list || [])
    } catch (error) {
      console.error('获取持卡人列表失败:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  // 处理添加持卡人
  const handleAddCardholder = useCallback(() => {
    navigate('/my/cardholder/add')
  }, [navigate])

  // 处理编辑持卡人（由后端 kyc.editable 控制）
  const handleEditCardholder = useCallback((cardholder: CardholderKycListItem) => {
    if (cardholder.kyc?.editable && cardholder.kyc?.id) {
      navigate('/my/cardholder/edit', { state: { kycId: cardholder.kyc.id } })
    }
  }, [navigate])

  // 处理点击持卡人卡片：可编辑则进入编辑页，否则进入查看页
  const handleCardholderClick = useCallback((cardholder: CardholderKycListItem) => {
    const kycId = cardholder.kyc?.id
    if (!kycId) return
    if (cardholder.kyc?.editable) {
      navigate('/my/cardholder/edit', { state: { kycId } })
    } else {
      navigate('/my/cardholder/view', { state: { kycId } })
    }
  }, [navigate])

  // 渲染持卡人卡片
  const renderCardholderItem = useCallback((item: CardholderKycListItem) => {
    const kyc = item.kyc
    const statusStyle = getStatusStyle(kyc?.kyc_status)
    const canEdit = kyc?.editable
    // 拼接姓名
    const name = [kyc?.last_name, kyc?.first_name].filter(Boolean).join(' ') || '--'

    return (
      <Ripple className="rounded-2xl">
        <div
          className="bg-white border border-solid border-[#EAECF0] rounded-2xl p-4 cursor-pointer"
          onClick={() => handleCardholderClick(item)}
        >
          <div className="flex items-center">
            {/* 左侧图标 */}
            <div className="w-12 h-12 rounded-full bg-[#F2F4F7] flex items-center justify-center flex-shrink-0">
              <img src={kyc?.identity_type === 'passport' ? '/icon/overseas.svg' : '/icon/mainland.svg'} alt="" className="w-6 h-6" />
            </div>

            {/* 中间内容 */}
            <div className="flex-1 ml-3 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-base font-medium text-[#1D2129]">{name}</span>
                {/* KYC状态标签 */}
                <span className={`text-xs px-2 py-0.5 rounded border border-solid ${statusStyle.bgColor} ${statusStyle.textColor} ${statusStyle.borderColor}`}>
                  {t(statusStyle.textKey)}
                </span>
              </div>
              <div className="text-sm text-[#86909C] mt-1">
                {t('证件号')} {kyc?.identity_number || '--'}
              </div>
            </div>

            {/* 右侧编辑按钮（kyc.editable 为 true 时显示） */}
            {canEdit && (
              <button
                className="flex items-center justify-center bg-transparent border-none cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation()
                  handleEditCardholder(item)
                }}
              >
                <img src="/icon/kyc-edit.svg" alt="edit" />
              </button>
            )}
          </div>
        </div>
      </Ripple>
    )
  }, [t, handleCardholderClick, handleEditCardholder])

  // 渲染骨架屏
  const renderSkeleton = useCallback(() => {
    return (
      <div className="bg-white border border-solid border-[#EAECF0] rounded-2xl p-4 mb-3">
        <div className="flex items-center">
          <SkeletonBox className="w-12 h-12 rounded-full flex-shrink-0" />
          <div className="flex-1 ml-3">
            <div className="flex items-center gap-2">
              <SkeletonBox className="w-20 h-5 rounded" />
              <SkeletonBox className="w-14 h-5 rounded" />
            </div>
            <SkeletonBox className="w-40 h-4 rounded mt-2" />
          </div>
        </div>
      </div>
    )
  }, [])

  // 是否可以添加更多持卡人
  const canAddMore = cardholders.length < MAX_CARDHOLDER_COUNT

  return (
    <PageLayout>
      <Header
        leftChildren={<Back />}
        centerChildren={<span className="text-base font-medium">{t('持卡人信息')}</span>}
      />

      <div className="px-5 pt-5 flex flex-col flex-1 pb-20">
        <RefreshList
          data={cardholders}
          renderItem={renderCardholderItem}
          keyExtractor={(item) => String(item.kyc?.id || Math.random())}
          loading={loading}
          hasMore={false}
          onRefresh={fetchCardholders}
          renderSkeleton={renderSkeleton}
          skeletonCount={5}
          finishedText={t('最多添加5个持卡人信息')}
          emptyComponent={
            <EmptyState
              text={t('暂无持卡人信息')}
              icon={<img src="/images/my/icons/no-card-user.svg" alt="" className="w-32 h-auto" />}
            />
          }
          itemClassName="space-y-3"
          className="flex-1"
        />

        {/* 最多添加提示 */}
        {/* {cardholders.length > 0 && (
          <div className="text-center text-sm text-[#86909C] mt-4 mb-4">
            {t('最多添加5个持卡人信息')}
          </div>
        )} */}

      </div>

      {/* 底部添加按钮 */}
      <FixedBottom className="bg-white">
        <CustomButton
          onClick={handleAddCardholder}
          disabled={!canAddMore}
        >
          {t('添加持卡人信息')}
        </CustomButton>
      </FixedBottom>
    </PageLayout>
  )
}

